import React from 'react';
import { useQuery } from 'react-query';
import ReactConfetti from 'react-confetti';
import { getTargetHistory } from 'config/api';
import {
  History,
  TargetType,
  MonthProps,
} from 'components/features/target-achievement-support/utils/type';
import Modal2 from 'components/commons/modal/Modal2';
import { targetTypeToIconImg } from 'components/features/target-achievement-support/utils/targetInfo';
import {
  addSignCommaFormat,
  commaFormat,
} from 'components/features/new-report/utils/formatter';
import Loading from 'components/commons/loading/Loading';
import { Percentage } from '../share/Percentage';

const Confetti = (): React.ReactElement => {
  return (
    <>
      <ReactConfetti
        {...{
          width: window.innerWidth,
          height: window.innerHeight,
          style: { zIndex: 9999 },
          gravity: 0.2,
          numberOfPieces: 1500,
          initialVelocityX: 13,
          initialVelocityY: 50,
          wind: 0,
          opacity: 70,
          recycle: false,
        }}
      />
    </>
  );
};

type ModalStatus = 2 | 3;
const MonthTargetModal = 3;
const LastMonthTargetModal = 2;

export const MonthlyTargetDecisionModal = ({
  hide,
  isShow,
  title,
  type,
  id,
  status,
}: {
  isShow: boolean;
  hide: () => void;
  title: string;
  type: TargetType;
  id: string;
  status: ModalStatus;
}): React.ReactElement => {
  const year = new Date().getFullYear();
  const month = (status === LastMonthTargetModal
    ? new Date().getMonth()
    : new Date().getMonth() + 1) as MonthProps;

  const { data: history, isLoading, isRefetching } = useQuery(
    'monthTargethistory',
    () => getTargetHistoryAPI(id, year, month),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (history === null || history === undefined || isLoading || isRefetching)
    return <></>;

  const currentAmount = history.achievementAmount;
  const targetAmountForDisplay = history.targetAmount;
  const num = (currentAmount / targetAmountForDisplay) * 100;
  const percentage = Number.isNaN(num) ? 0 : num;
  const isAchieved =
    0 <= currentAmount - targetAmountForDisplay || targetAmountForDisplay === 0;

  return (
    <>
      {isShow && <Confetti />}
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row max-h-modal overflow-y-auto">
            <div
              className="icon-close ml-auto text-gray-500 cursor-pointer"
              onClick={hide}
            />
          </div>
          <div className="font-bold text-bluegray-700 text-lg text-center">
            {((): React.ReactElement => {
              switch (status) {
                case MonthTargetModal:
                  return <>今月の目標が決まりました</>;
                case LastMonthTargetModal:
                  return <>目標を達成しました</>;
              }
            })()}
          </div>
          <div className="p-3 mt-2 border border-bluegray-300 rounded">
            <div className={`flex flex-row gap-2 items-center`}>
              <div>
                <img
                  src={targetTypeToIconImg(type)}
                  alt="目標アイコン"
                  className={`w-12 h-12 rounded-full border`}
                  style={{ borderColor: '#dee5ee', objectFit: 'cover' }}
                />
              </div>
              <div className={`font-bold text-lg`}>{title}</div>
            </div>
            <div className="z-0 relative flex flex-row mt-1 mb-2">
              <div
                className={`py-2 px-3 bg-white border border-bluegray-300 relative ml-auto rounded`}>
                <div
                  className="border-b border-r border-bluegray-300 bg-white w-2 h-2 absolute"
                  style={{
                    transform: 'rotate(45deg)',
                    right: '2rem',
                    top: '1.8rem',
                    zIndex: 10,
                  }}></div>
                <div className="z-20 rounded bg-white absolute right-0 top-0 left-0 bottom-0"></div>
                <div
                  className={`flex flex-row items-center text-bluegray-700 h-4 z-30`}>
                  <div className="z-20 text-bluegray-700">
                    {((): React.ReactElement => {
                      switch (status) {
                        case MonthTargetModal:
                          return (
                            <>
                              <span className="text-xs">今月の目標</span>
                              <span className="ml-3 mr-1 text-xl font-bold">
                                {commaFormat(targetAmountForDisplay, {
                                  numberOfDecimals: 0,
                                })}
                              </span>
                            </>
                          );
                        case LastMonthTargetModal:
                          return (
                            <>
                              <span className="text-xs">先月の実績</span>
                              <span className="ml-3 mr-1 text-xl font-bold">
                                {commaFormat(currentAmount, {
                                  numberOfDecimals: 0,
                                })}
                              </span>
                            </>
                          );
                      }
                    })()}
                    <span className="text-xs font-bold">円</span>
                  </div>
                </div>
              </div>
            </div>
            <Percentage
              {...{
                percentage,
                backgroundColorTailwind: 'bg-gray-300',
                foregroundColorTailwind: isAchieved ? null : 'bg-blue-500',
                isShowPercentageStr: true,
                isAchieved: isAchieved,
              }}
            />
            <div className="flex mt-2">
              <div className="ml-auto text-bluegray-700">
                {((): React.ReactElement => {
                  switch (status) {
                    case MonthTargetModal:
                      return (
                        <>
                          <span className="text-xs">現在の実績</span>
                          <span className="ml-3 mr-1 text-lg font-bold">
                            {commaFormat(currentAmount, {
                              numberOfDecimals: 0,
                            })}
                          </span>
                        </>
                      );
                    case LastMonthTargetModal:
                      return (
                        <>
                          <span className="text-xs">先月の目標金額</span>
                          <span className="ml-3 mr-1 text-lg font-bold">
                            {addSignCommaFormat(targetAmountForDisplay, {
                              numberOfDecimals: 0,
                            })}
                          </span>
                        </>
                      );
                  }
                })()}
                <span className="text-base">円</span>
              </div>
            </div>
          </div>
          <div className="mt-2 text-sm text-center text-bluegray-700">
            {((): React.ReactElement => {
              switch (status) {
                case MonthTargetModal:
                  return <>今月もムリなく進めていきましょう</>;
                case LastMonthTargetModal:
                  return <>目標達成おめでとうございます！</>;
              }
            })()}
          </div>
          <div className="m-4 flex flex-col">
            <button
              className="text-sm text-bluegray-500 py-2 flex flex-row items-center justify-center mt-2"
              onClick={hide}>
              閉じる
            </button>
          </div>
        </div>
      </Modal2>
    </>
  );
};

const getTargetHistoryAPI = async (id: string, year: number, month: number) => {
  const period = `${year}${month.toString().padStart(2, '0')}`;
  const ret = await getTargetHistory(id, period);

  return {
    id: ret.ID,
    achievementAmount: ret.AchievementAmount,
    targetAmount: ret.TargetAmount,
    from: ret.From,
    to: ret.To,
    transitions: ret.Transitions.map(item => {
      return {
        assetAmount: item.AssetAmount,
        benchmarkAmount: item.BenchmarkAmount,
        date: item.Date,
        incorrect: item.Incorrect,
      };
    }),
  } as History;
};
